import React, { useState, useEffect } from "react";
import { message, Spin } from "antd";
import Breadcrumbs from "../../../../components/Breadcrumbs";
import {
  useGetSmsSettingsQuery,
  useSaveSmsSettingsMutation,
  useTestSmsMutation,
} from "../../../../services/settings.service";
import { smsSettingsSchema, testSmsSchema } from "../../../../utils/validationSchemas";
import { useFormValidation } from "../../../../hooks/useFormValidation";

const SmsConfig = () => {
  const [formData, setFormData] = useState({
    termiiApiKey: "",
    termiiSenderId: "",
    termiiChannel: "generic",
    enableSms: false,
  });
  const [testPhone, setTestPhone] = useState("");

  // Validation Hooks
  const { errors, validateForm, validateField, touchField, hasFieldError, getFieldError } = useFormValidation(smsSettingsSchema);
  const testSmsValidation = useFormValidation(testSmsSchema);

  // API Hooks
  const { data: smsSettingsData, isLoading } = useGetSmsSettingsQuery();
  const [saveSettings, { isLoading: isSaving }] = useSaveSmsSettingsMutation();
  const [sendTestSms, { isLoading: isTesting }] = useTestSmsMutation();

  // Load initial data
  useEffect(() => {
    if (smsSettingsData?.data) {
      const data = smsSettingsData.data;
      setFormData({
        termiiApiKey: data.termiiApiKey || "",
        termiiSenderId: data.termiiSenderId || "",
        termiiChannel: data.termiiChannel || "generic",
        enableSms: data.enableSms || false,
      });
    }
  }, [smsSettingsData]);

  // Handle input change
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    const newValue = type === 'checkbox' ? checked : value;
    const newFormData = {
      ...formData,
      [name]: newValue
    };
    setFormData(newFormData);

    // Validate field on change
    validateField(name, newValue, newFormData);
  };

  // Handle blur event
  const handleBlur = (fieldName) => {
    touchField(fieldName);
    validateField(fieldName, formData[fieldName], formData);
  };

  // Handle form submit
  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate entire form
    const { isValid } = await validateForm(formData);

    if (!isValid) {
      // Validation errors will be displayed inline
      return;
    }

    try {
      // If validation passes, save settings
      await saveSettings(formData).unwrap();
      message.success('SMS settings saved successfully');
    } catch (error) {
      // Handle API errors
      message.error(error?.data?.message || 'Failed to save SMS settings');
    }
  };

  // Handle test SMS
  const handleTestSms = async () => {
    // Validate test SMS data
    const testSmsResult = await testSmsValidation.validateForm({
      phone: testPhone,
      termiiApiKey: formData.termiiApiKey,
      termiiSenderId: formData.termiiSenderId,
    });

    if (!testSmsResult.isValid) {
      const errorMessages = Object.values(testSmsResult.errors);
      message.error(errorMessages[0] || 'Please check the SMS configuration');
      return;
    }

    try {
      // Send test SMS
      await sendTestSms({
        phone: testPhone,
        ...formData
      }).unwrap();
      message.success(`Test SMS sent successfully to ${testPhone}`);
      setTestPhone("");
    } catch (error) {
      // Handle API errors
      message.error(error?.data?.message || 'Failed to send test SMS');
    }
  };

  if (isLoading) {
    return (
      <div className="page-wrapper">
        <div className="content container-fluid">
          <div style={{ textAlign: 'center', padding: '50px' }}>
            <Spin size="large" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="page-wrapper">
        {/* Page Content */}
        <div className="content container-fluid">
          <div className="row">
            <div className="col-md-8 offset-md-2">
              {/* Page Header */}
              <Breadcrumbs maintitle="SMS Configuration (Termii)" />
              {/* /Page Header */}

              <form onSubmit={handleSubmit}>
                {/* Enable/Disable SMS */}
                <div className="settings-widget mb-4">
                  <div className="h3 card-title with-switch">
                    Enable SMS Notifications
                    <div className="onoffswitch">
                      <input
                        type="checkbox"
                        name="enableSms"
                        className="onoffswitch-checkbox"
                        id="enable_sms"
                        checked={formData.enableSms}
                        onChange={handleInputChange}
                      />
                      <label className="onoffswitch-label" htmlFor="enable_sms">
                        <span className="onoffswitch-inner" />
                        <span className="onoffswitch-switch" />
                      </label>
                    </div>
                  </div>
                  <p className="text-muted">
                    Enable or disable SMS notifications using Termii API
                  </p>
                </div>

                {/* Termii API Settings */}
                <h4 className="page-title">Termii API Configuration</h4>
                <div className="row">
                  <div className="col-sm-12">
                    <div className="input-block mb-3">
                      <label className="col-form-label">
                        Termii API Key {formData.enableSms && <span className="text-danger">*</span>}
                      </label>
                      <input
                        className={`form-control ${hasFieldError('termiiApiKey') ? 'is-invalid' : ''}`}
                        type="text"
                        name="termiiApiKey"
                        value={formData.termiiApiKey}
                        onChange={handleInputChange}
                        onBlur={() => handleBlur('termiiApiKey')}
                        placeholder="Enter your Termii API Key"
                        disabled={!formData.enableSms}
                      />
                      {getFieldError('termiiApiKey') && (
                        <div className="text-danger small mt-1">
                          {getFieldError('termiiApiKey')}
                        </div>
                      )}
                      <small className="form-text text-muted">
                        Get your API key from{" "}
                        <a href="https://termii.com" target="_blank" rel="noopener noreferrer">
                          Termii Dashboard
                        </a>
                      </small>
                    </div>
                  </div>
                  <div className="col-sm-6">
                    <div className="input-block mb-3">
                      <label className="col-form-label">
                        Sender ID {formData.enableSms && <span className="text-danger">*</span>}
                      </label>
                      <input
                        className={`form-control ${hasFieldError('termiiSenderId') ? 'is-invalid' : ''}`}
                        type="text"
                        name="termiiSenderId"
                        value={formData.termiiSenderId}
                        onChange={handleInputChange}
                        onBlur={() => handleBlur('termiiSenderId')}
                        placeholder="e.g., HRMS"
                        disabled={!formData.enableSms}
                        maxLength={11}
                      />
                      {getFieldError('termiiSenderId') && (
                        <div className="text-danger small mt-1">
                          {getFieldError('termiiSenderId')}
                        </div>
                      )}
                      <small className="form-text text-muted">
                        Maximum 11 characters (alphanumeric)
                      </small>
                    </div>
                  </div>
                  <div className="col-sm-6">
                    <div className="input-block mb-3">
                      <label className="col-form-label">Channel</label>
                      <select
                        className={`form-control ${hasFieldError('termiiChannel') ? 'is-invalid' : ''}`}
                        name="termiiChannel"
                        value={formData.termiiChannel}
                        onChange={handleInputChange}
                        onBlur={() => handleBlur('termiiChannel')}
                        disabled={!formData.enableSms}
                      >
                        <option value="generic">Generic</option>
                        <option value="dnd">DND</option>
                        <option value="whatsapp">WhatsApp</option>
                      </select>
                      {getFieldError('termiiChannel') && (
                        <div className="text-danger small mt-1">
                          {getFieldError('termiiChannel')}
                        </div>
                      )}
                      <small className="form-text text-muted">
                        Select the messaging channel
                      </small>
                    </div>
                  </div>
                </div>

                {/* Test SMS Section */}
                <h4 className="page-title m-t-30">Test SMS Configuration</h4>
                <div className="row">
                  <div className="col-sm-8">
                    <div className="input-block mb-3">
                      <label className="col-form-label">Test Phone Number</label>
                      <input
                        className="form-control"
                        type="tel"
                        value={testPhone}
                        onChange={(e) => setTestPhone(e.target.value)}
                        placeholder="e.g., +2348012345678"
                        disabled={!formData.enableSms}
                      />
                      <small className="form-text text-muted">
                        Enter phone number with country code (e.g., +234...)
                      </small>
                    </div>
                  </div>
                  <div className="col-sm-4">
                    <div className="input-block mb-3">
                      <label className="col-form-label">&nbsp;</label>
                      <button
                        type="button"
                        className="btn btn-secondary w-100"
                        onClick={handleTestSms}
                        disabled={isTesting || !testPhone || !formData.enableSms}
                      >
                        {isTesting ? 'Sending...' : 'Send Test SMS'}
                      </button>
                    </div>
                  </div>
                </div>

                {/* API Information */}
                <div className="alert alert-info">
                  <h5><i className="la la-info-circle"></i> Termii API Information</h5>
                  <ul className="mb-0">
                    <li>Termii provides SMS messaging services across Africa</li>
                    <li>Make sure you have sufficient credits in your Termii account</li>
                    <li>The Sender ID must be registered and approved in your Termii dashboard</li>
                    <li>Test the configuration before enabling SMS notifications</li>
                  </ul>
                </div>

                <div className="submit-section">
                  <button
                    className="btn btn-primary submit-btn"
                    type="submit"
                    disabled={isSaving}
                  >
                    {isSaving ? 'Saving...' : 'Save & Update'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
        {/* /Page Content */}
      </div>
    </>
  );
};

export default SmsConfig;
